<template>
    <div class="resort-guide" :class="{ 'resort-guide-common': isCommonDesign }" v-if="loaded">
        <div class="container">
            <div class="row">
                <div class="col-md-12">
                    <div class="resort_tabs">
                        <div class="tx-dftabs-plugin1">
                            <tabs :items="tabs" v-model="active_tab"></tabs>
                            <div class="tx-dftabs-tabContents" id="tx-dftabs-tabContents-1">
                                <div class="tx-dftabs-tabContent tx-dftabs-tabContentSelected" v-html="activeTabHtml"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Resort from "../../src/Resort";
    import Tabs from './components/Tabs';
    import LocationsMap from "./LocationsMap";

    export default {
        props: {
            uuid: {
                type: String
            },
            mode: {
                type: String,
                default: 'legacy'
            }
        },
        data: function ()
        {
            return {
                resort: new Resort,
                loaded: false,
                active_tab: 'Overview',
            }
        },
        mounted: function ()
        {
            Resort.$query().find(this.uuid).then((resort) =>
            {
                this.resort = resort;
                this.loaded = true;

                if (this.tabs.length) {
                    this.active_tab = this.tabs[0].value;
                }

                this.applyTabFromHash();
            });

            window.addEventListener('hashchange', this.applyTabFromHash);
        },
        beforeDestroy: function ()
        {
            window.removeEventListener('hashchange', this.applyTabFromHash);
        },
        computed: {
            isCommonDesign: function () {
                return this.mode === 'common';
            },
            tabs: function () {
                let tabs = [];

                for (let content of this.resort.$attributes.content)
                {
                    if (content.disabled) continue;
                    tabs.push({
                        value: content.name,
                        label: content.name
                    })
                }

                return tabs;
            },
            activeTabHtml: function () {
                const tab = this.resort.$attributes.content.find((content) => content.name === this.active_tab);
                const rawContent = tab ? (tab.content || '') : '';

                if (!this.isCommonDesign) {
                    return rawContent;
                }

                return this.normalizeCommonContent(rawContent);
            }
        },
        watch: {
            active_tab: function (value)
            {
                this.updateHashFromTab(value);
            }
        },
        methods: {
            escapeHtml: function (value)
            {
                return String(value || '')
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&#039;');
            },
            normalizeCommonContent: function (html)
            {
                const value = (html || '').toString().trim();

                if (!value) {
                    return '<p class="rg-empty">Content coming soon.</p>';
                }

                const hasHtmlTags = /<[^>]+>/.test(value);

                if (!hasHtmlTags) {
                    return value
                        .split(/\n{2,}/)
                        .map((paragraph) => `<p>${this.escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
                        .join('');
                }

                if (typeof window.DOMParser === 'undefined') {
                    return value;
                }

                const parser = new window.DOMParser();
                const doc = parser.parseFromString(`<div>${value}</div>`, 'text/html');
                const root = doc.body.firstElementChild;

                if (!root) {
                    return value;
                }

                const allowedAttributes = ['href', 'src', 'alt', 'title', 'target', 'rel', 'class', 'style'];
                const elements = root.querySelectorAll('*');

                elements.forEach((node) => {
                    Array.from(node.attributes || []).forEach((attr) => {
                        if (!allowedAttributes.includes(attr.name.toLowerCase())) {
                            node.removeAttribute(attr.name);
                        }
                    });

                    if (node.tagName.toLowerCase() === 'a' && node.getAttribute('target') === '_blank') {
                        node.setAttribute('rel', 'noopener noreferrer');
                    }
                });

                return root.innerHTML;
            },
            normalizeTabName: function (value)
            {
                if (!value) {
                    return '';
                }

                return value
                    .toString()
                    .trim()
                    .toLowerCase()
                    .replace(/&/g, 'and')
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/^-+|-+$/g, '');
            },
            applyTabFromHash: function ()
            {
                if (!this.tabs.length) {
                    return;
                }

                const rawHash = (window.location.hash || '').replace(/^#/, '');
                if (!rawHash) {
                    return;
                }

                const decodedHash = decodeURIComponent(rawHash).trim();
                const normalizedHash = this.normalizeTabName(decodedHash);

                const legacyMatch = /^tab[-_]?(\d+)$/i.exec(decodedHash);
                if (legacyMatch) {
                    const tabIndex = parseInt(legacyMatch[1], 10);
                    if (tabIndex >= 0 && tabIndex < this.tabs.length) {
                        this.active_tab = this.tabs[tabIndex].value;
                    }
                    return;
                }

                const matchedTab = this.tabs.find((tab) => {
                    const normalizedName = this.normalizeTabName(tab.value);
                    return normalizedName === normalizedHash || tab.value.toLowerCase() === decodedHash.toLowerCase();
                });

                if (matchedTab) {
                    this.active_tab = matchedTab.value;
                }
            },
            updateHashFromTab: function (tabName)
            {
                if (!tabName) {
                    return;
                }

                const normalized = this.normalizeTabName(tabName);
                const nextHash = normalized ? `#${normalized}` : '';

                if (nextHash && window.location.hash !== nextHash) {
                    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${nextHash}`);
                }
            }
        },
        components: {
            'tabs': Tabs,
            'locations-map': LocationsMap
        }
    }
</script>

<style>
    /* Fix: style.css sets .resort_tabs { margin-top: -36px } for old TYPO3 layout
       where the tab menu was OUTSIDE .resort_tabs. Now tabs are INSIDE, so
       the negative margin hides the tab bar behind the element above it. */
    .resort-guide-common .resort_tabs {
        margin-top: 0 !important;
        padding-top: 20px !important;
    }

    .resort-guide-common .tx-dftabs-plugin1,
    .resort-guide-common .tx-dftabs-tabContents,
    .resort-guide-common .tx-dftabs-tabContent {
        width: 100%;
        max-width: none;
        overflow: visible !important;
    }

    .resort-guide-common .tx-dftabs-tabContents {
        background: transparent !important;
        margin-top: 0 !important;
        padding: 0 !important;
        border: 0 !important;
    }

    .resort-guide-common .tx-dftabs-tabContent {
        margin: 0;
        padding: 24px 0 0 0 !important;
        background: transparent;
        border: 0;
    }

    .resort-guide-common .tx-dftabs-tabContent h1,
    .resort-guide-common .tx-dftabs-tabContent h2,
    .resort-guide-common .tx-dftabs-tabContent h3,
    .resort-guide-common .tx-dftabs-tabContent h4 {
        color: #1f2a37;
        line-height: 1.3;
        margin-top: 24px;
        margin-bottom: 12px;
    }

    .resort-guide-common .tx-dftabs-tabContent p,
    .resort-guide-common .tx-dftabs-tabContent li {
        color: #2f3d4a;
        font-size: 16px;
        line-height: 1.75;
    }

    .resort-guide-common .tx-dftabs-tabContent ul,
    .resort-guide-common .tx-dftabs-tabContent ol {
        padding-left: 22px;
    }

    .resort-guide-common .tx-dftabs-tabContent a {
        color: #8b5e1a;
        text-decoration: underline;
    }

    .resort-guide-common .tx-dftabs-tabContent img {
        max-width: 100%;
        height: auto;
        border-radius: 0 !important;
    }

    .resort-guide-common .resort-guide-section {
        margin-bottom: 28px;
        padding-bottom: 28px;
        border-bottom: 0 !important;
    }

    .resort-guide-common .resort-guide-section h2 {
        margin-bottom: 18px;
        color: #1f2a37;
    }

    .resort-guide-common .rg-grid-4 {
        display: grid !important;
        grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
        gap: 20px !important;
    }

    .resort-guide-common .rg-grid-2 {
        display: grid !important;
        grid-template-columns: 1fr 1fr !important;
        gap: 4px !important;
        align-items: stretch !important;
        /* Slightly wider than container, centered */
        width: 90vw !important;
        margin-left: calc(50% - 45vw) !important;
        margin-right: calc(50% - 45vw) !important;
    }

    /* 2-col: fixed equal height, full-width cover — clean and aligned */
    .resort-guide-common .rg-grid-2 figure {
        margin: 0 !important;
        overflow: hidden !important;
        line-height: 0 !important;
    }

    .resort-guide-common .rg-grid-2 img,
    .resort-guide-common .rg-grid-2 .rg-card-image {
        width: 100% !important;
        height: 67vw !important;
        object-fit: cover !important;
        object-position: center center !important;
        display: block !important;
    }

    /* Legacy generated HTML — same 90vw centered */
    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"],
    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns: 1fr 1fr"] {
        gap: 4px !important;
        align-items: stretch !important;
        width: 90vw !important;
        margin-left: calc(50% - 45vw) !important;
        margin-right: calc(50% - 45vw) !important;
    }

    /* Legacy generated HTML fallback (before rg-grid classes) */
    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"],
    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns: 1fr 1fr"],
    .resort-guide-common .resort-guide-section div[style*="grid-template-columns:repeat(4"],
    .resort-guide-common .resort-guide-section div[style*="grid-template-columns: repeat(4"] {
        display: grid !important;
        width: 100%;
    }

    .resort-guide-common .rg-card-image {
        width: 100% !important;
        height: 420px !important;
        object-fit: cover !important;
        border-radius: 0 !important;
    }

    .resort-guide-common .rg-card-image--portrait {
        height: 520px !important;
    }

    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"] img,
    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns: 1fr 1fr"] img,
    .resort-guide-common .resort-guide-section div[style*="grid-template-columns:repeat(4"] img,
    .resort-guide-common .resort-guide-section div[style*="grid-template-columns: repeat(4"] img {
        width: 100% !important;
        object-fit: cover !important;
        border-radius: 0 !important;
    }

    /* Enforce perfectly even card heights for all 2-column guide grids, including legacy saved HTML */
    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"] figure {
        margin: 0 !important;
        overflow: hidden !important;
        line-height: 0 !important;
    }

    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"] img {
        width: 100% !important;
        height: 67vw !important;
        object-fit: cover !important;
        object-position: center center !important;
        display: block !important;
    }

    .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"] figcaption {
        min-height: 24px !important;
        line-height: 1.4;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .resort-guide-common .resort-guide-image-wrap {
        padding: 8px;
        background: #fbfbfb;
        border: 1px solid #e7dfd2;
        border-radius: 8px;
        text-align: center;
    }

    .resort-guide-common .resort-guide-image {
        width: 100%;
        height: auto;
        border-radius: 8px;
        object-fit: cover;
    }

    .resort-guide-common .col-md-8, .resort-guide-common .col-md-4 {
        margin-bottom: 12px;
    }

    .resort-guide-common .rg-empty {
        color: #6b7280;
        font-style: italic;
    }

    @media (max-width: 1180px) {
        .resort-guide-common .rg-grid-4 {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        }

        .resort-guide-common .resort-guide-section div[style*="grid-template-columns:repeat(4"],
        .resort-guide-common .resort-guide-section div[style*="grid-template-columns: repeat(4"] {
            grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        }
    }

    @media (max-width: 992px) {
        .resort-guide-common .resort-guide-section {
            margin-bottom: 28px;
            padding-bottom: 28px;
            padding-top: 20px !important;
        }

        .resort-guide-common .rg-grid-2 img,
        .resort-guide-common .rg-grid-2 .rg-card-image,
        .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"] img {
            height: 67vw !important;
        }

        .resort-guide-common .rg-grid-4 {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 14px !important;
        }

        .resort-guide-common .resort-guide-section div[style*="grid-template-columns:repeat(4"],
        .resort-guide-common .resort-guide-section div[style*="grid-template-columns: repeat(4"] {
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 14px !important;
        }

        .resort-guide-common .rg-card-image {
            height: 300px !important;
        }

        .resort-guide-common .rg-card-image--portrait {
            height: 380px !important;
        }

        .resort-guide-common .resort-guide-section div[style*="grid-template-columns:repeat(4"] img,
        .resort-guide-common .resort-guide-section div[style*="grid-template-columns: repeat(4"] img {
            height: 300px !important;
        }
    }

    @media (max-width: 767px) {
        .resort-guide-common .tx-dftabs-tabContent {
            padding: 0;
        }

        .resort-guide-common .resort-guide-section {
            padding-top: 28px !important;
        }

        .resort-guide-common .rg-grid-2 {
            grid-template-columns: 1fr !important;
            gap: 4px !important;
            /* Keep 90vw centered on mobile too */
            width: 90vw !important;
            margin-left: calc(50% - 45vw) !important;
            margin-right: calc(50% - 45vw) !important;
        }

        .resort-guide-common .rg-grid-4 {
            grid-template-columns: 1fr !important;
            gap: 4px !important;
        }

        /* On mobile, stacked 2-col images — portrait height, full column width */
        .resort-guide-common .rg-grid-2 img,
        .resort-guide-common .rg-grid-2 .rg-card-image,
        .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"] img {
            height: 120vw !important;
        }

        .resort-guide-common .resort-guide-section > div[style*="grid-template-columns:1fr 1fr"],
        .resort-guide-common .resort-guide-section > div[style*="grid-template-columns: 1fr 1fr"],
        .resort-guide-common .resort-guide-section div[style*="grid-template-columns:repeat(4"],
        .resort-guide-common .resort-guide-section div[style*="grid-template-columns: repeat(4"] {
            grid-template-columns: 1fr !important;
            gap: 4px !important;
            width: 90vw !important;
            margin-left: calc(50% - 45vw) !important;
            margin-right: calc(50% - 45vw) !important;
        }

        .resort-guide-common .rg-card-image,
        .resort-guide-common .rg-card-image--portrait {
            height: 240px !important;
        }

        .resort-guide-common .resort-guide-section div[style*="grid-template-columns:repeat(4"] img,
        .resort-guide-common .resort-guide-section div[style*="grid-template-columns: repeat(4"] img {
            height: 200px !important;
        }

        /* On mobile the parent already fills 100% — reset hero to simple full-width block */
        .resort-guide-common figure[data-rg-hero] {
            position: static !important;
            left: auto !important;
            right: auto !important;
            width: 100% !important;
            max-width: 100% !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
        }

        .resort-guide-common figure[data-rg-hero] img {
            width: 100% !important;
            height: 260px !important;
            object-fit: cover !important;
        }
    }

    /* Prevent 100vw hero from causing horizontal scroll due to scrollbar width on desktop */
    .resort-guide {
        overflow-x: hidden;
    }
</style>
